import { Card, CardContent, Box, Typography, IconButton, Divider, Badge, Tooltip } from "@mui/joy";
import { Minus, Plus, Trash2, CircleAlert, RefreshCcw } from "lucide-react";
import { motion } from "framer-motion";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface LaborSelection {
    quantity: number;
    unitPrice?: number;
}

interface LaborCardProps {
    /** The labor service object from the backend (Id, Name, Unit, Description) */
    laborItem: any;
    /** The current selection state for this item (quantity, price) */
    selection?: LaborSelection;
    onQuantityChange: (serviceId: string, direction: "add" | "remove") => void;
    onRemove: (serviceId: string) => void;
    onReevaluate?: (serviceId: string) => void;
    /** Optional price range to display when no confirmed unit price is available */
    priceRange?: { min: number; max: number };
}

// ─── Pure Display Helpers ─────────────────────────────────────────────────────

function getPriceDisplay(selection?: LaborSelection, priceRange?: { min: number; max: number }) {
    const unitPrice = Number(selection?.unitPrice ?? 0);
    if (unitPrice > 0) return { text: `$${unitPrice.toFixed(2)}`, label: "Unit Price" };
    if (priceRange) return { text: `$${priceRange.min.toFixed(2)} - $${priceRange.max.toFixed(2)}`, label: "Price Range" };
    return { text: "-", label: "Price" };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function QuantityControl({
    quantity,
    onAdd,
    onRemove,
}: {
    quantity: number;
    onAdd: () => void;
    onRemove: () => void;
}) {
    return (
        <Box
            sx={{
                display: "flex",
                borderRadius: "40px",
                p: 0.2,
                pb: 0.15,
                alignItems: "center",
                gap: 1,
                border: "1px solid",
                borderColor: "divider",
            }}
        >
            <IconButton
                variant="soft"
                size="sm"
                color="danger"
                onClick={onRemove}
                sx={{ border: "1px solid", borderColor: "divider", "--IconButton-size": "32px", borderRadius: "50%" }}
            >
                <Minus size={12} />
            </IconButton>

            <Typography
                level="body-sm"
                sx={{ fontWeight: "md", mb: 0, minWidth: 20, textAlign: "center" }}
                component="span"
            >
                {quantity}
            </Typography>

            <Badge badgeContent={quantity} variant="soft" color="warning">
                <IconButton
                    variant="soft"
                    color="success"
                    size="sm"
                    onClick={onAdd}
                    sx={{ border: "1px solid", borderColor: "divider", "--IconButton-size": "32px", borderRadius: "50%" }}
                >
                    <Plus size={12} />
                </IconButton>
            </Badge>
        </Box>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function LaborCard({ laborItem, selection, onQuantityChange, onRemove, onReevaluate, priceRange }: LaborCardProps) {
    const quantity = selection?.quantity ?? 0;
    const hasZeroQuantity = quantity === 0;
    const { text: priceText, label: priceLabel } = getPriceDisplay(selection, priceRange);

    return (
        <Card
            sx={{
                p: 1.5,
                px: 2,
                borderRadius: 10,
                border: "1px solid",
                borderColor: "divider",
                zIndex: 2,
            }}
            variant="soft"
        >
            <CardContent>
                <Box display="flex" justifyContent="space-between" gap={3}>
                    {/* LEFT: Name + unit + warning */}
                    <Box flex={1} display="flex" flexDirection="column" justifyContent="center">
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                                {laborItem.Name}
                            </Typography>
                            {laborItem.Unit && (
                                <Typography level="body-xs" sx={{ color: "text.secondary" }}>
                                    ({laborItem.Unit})
                                </Typography>
                            )}
                            {hasZeroQuantity && (
                                <Tooltip title="Quantity is Zero" arrow variant="solid" color="warning">
                                    <motion.div
                                        animate={{ opacity: [1, 0.5, 1], scale: [1, 1.1, 1] }}
                                        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                                        style={{ display: "flex", alignItems: "center" }}
                                    >
                                        <CircleAlert size={16} color="#ed6c02" />
                                    </motion.div>
                                </Tooltip>
                            )}
                        </Box>
                        {laborItem.Description && (
                            <Typography level="body-xs" sx={{ color: "text.secondary", mt: 0.25 }}>
                                {laborItem.Description}
                            </Typography>
                        )}
                    </Box>

                    {/* MIDDLE: Quantity stepper */}
                    <Box display="flex" alignItems="center">
                        <QuantityControl
                            quantity={quantity}
                            onAdd={() => onQuantityChange(laborItem.Id, "add")}
                            onRemove={() => onQuantityChange(laborItem.Id, "remove")}
                        />
                    </Box>

                    <Divider orientation="vertical" />

                    {/* RIGHT: Price */}
                    <Box display="flex" flexDirection="column" justifyContent="center" minWidth={140}>
                        <Typography sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{priceText}</Typography>
                        <Typography level="body-xs">{priceLabel}</Typography>
                    </Box>

                    <Divider orientation="vertical" />

                    {/* ACTIONS */}
                    <Box display="flex" alignItems="center" gap={1}>
                        {onReevaluate && (
                            <IconButton
                                size="sm"
                                color="primary"
                                variant="soft"
                                onClick={() => onReevaluate(laborItem.Id)}
                                title="Re-evaluate formula"
                                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 6, "--IconButton-size": "26px" }}
                            >
                                <RefreshCcw size={12} />
                            </IconButton>
                        )}
                        <IconButton
                            size="sm"
                            color="danger"
                            variant="soft"
                            onClick={() => onRemove(laborItem.Id)}
                            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 6, "--IconButton-size": "26px" }}
                        >
                            <Trash2 size={12} />
                        </IconButton>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}
